// Final version of client.js for production
import axios from 'axios';
import React from 'react';
import { TimeValue } from '../utils/TimeValue';
import { getSubscriptionInfo } from '../utils/subscription';

import { getApiBaseUrl } from '../utils/apiConfig';

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = window.location.hostname === 'medinsight.pro';
const isStaging =
  window.location.hostname.includes('staging.medinsight.pro') ||
  window.location.hostname.includes('-git-dev-') ||
  window.location.hostname.includes('-jamies-projects-') ||
  window.location.hostname.includes('medinsight-pro-dev.onrender.com');

// Log environment detection when in development
if (isDevelopment) {
  console.log('🌐 Environment detection:', {
    hostname: window.location.hostname,
    isDevelopment,
    isStaging,
    isProduction,
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_URL: process.env.REACT_APP_API_URL
  });
}

// Determine API URL based on environment using shared utility
const apiBaseURL = getApiBaseUrl();

if (isDevelopment || isStaging) {
  console.log('📦 Final Api baseURL:', apiBaseURL);
}

// Create an Axios instance with common configuration
const apiClient = axios.create({
  baseURL: apiBaseURL,
  timeout: 15000, // Increased timeout to 15 seconds for better reliability
  withCredentials: true, // Important for CORS with credentials
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add detailed logging interceptors
apiClient.interceptors.request.use(config => {
  console.group('🚀 API Request');
  console.log('URL:', config.url);
  console.log('Method:', config.method);
  console.log('Headers:', config.headers);
  if (config.data) {
    console.log('Request Data:', config.data);
    console.log('Request Data (JSON):', JSON.stringify(config.data, null, 2));
  }
  console.groupEnd();
  return config;
}, error => {
  console.error('Request Error:', error);
  return Promise.reject(error);
});

// HELPER FUNCTION: Check if a request should be excluded from processing
const shouldSkipProcessing = (config) => {
  // Skip for auth endpoints
  if (config.url?.includes('/auth/')) {
    if (isDevelopment) {
      console.log('⚠️ Skipping data processing for auth request:', config.url);
    }
    return true;
  }

  // Skip for form-urlencoded content
  const contentType = config.headers?.['Content-Type'] || '';
  if (contentType.includes('application/x-www-form-urlencoded')) {
    if (isDevelopment) {
      console.log('⚠️ Skipping data processing for form-urlencoded request:', config.url);
    }
    return true;
  }

  // Skip for multipart/form-data content
  if (contentType.includes('multipart/form-data')) {
    if (isDevelopment) {
      console.log('⚠️ Skipping data processing for multipart/form-data request:', config.url);
    }
    return true;
  }

  return false;
};

// Add recursive TimeValue processing interceptor
apiClient.interceptors.request.use(config => {
  try {
    // Skip processing for specific requests
    if (shouldSkipProcessing(config)) {
      return config;
    }

    // Recursively process data to convert TimeValue objects to UTC strings
    const processData = (data) => {
      if (!data) return data;

      // Handle arrays
      if (Array.isArray(data)) {
        return data.map(item => processData(item));
      }

      // Handle objects
      if (typeof data === 'object') {
        // Special case for TimeValue instances
        if (data instanceof TimeValue) {
          return data.toUTC().value;
        }

        // Avoid processing React components or other special objects
        if (data instanceof Error || React.isValidElement(data)) {
          return data;
        }

        // Process regular objects recursively
        const processed = {};
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            processed[key] = processData(data[key]);
          }
        }
        return processed;
      }

      // Return primitives as is
      return data;
    };

    // Process request data if it exists and is not a string
    if (config.data && typeof config.data !== 'string') {
      // Process the data recursively
      const processedData = processData(config.data);

      // Update the config
      config.data = processedData;

      // Log if we're in development mode
      if (isDevelopment) {
        console.group('🔄 Recursive TimeValue Processing');
        console.log('Endpoint:', config.url);
        console.log('Method:', config.method);
        console.log('Processed request data:', config.data);
        console.groupEnd();
      }
    } else if (typeof config.data === 'string') {
      // If it's a string (already stringified), safely try to process it
      try {
        const parsedData = JSON.parse(config.data);
        const processedData = processData(parsedData);
        config.data = JSON.stringify(processedData);
      } catch (err) {
        // If parsing fails, it's probably not JSON, so just leave it as is
        if (isDevelopment) {
          console.log('⚠️ Not processing string data that is not valid JSON:', config.url);
        }
      }
    }
  } catch (err) {
    // If any error occurs during processing, log it but allow the request to continue
    console.error('Error in TimeValue processing interceptor:', err);
  }

  return config;
});

// Add request interceptor for authentication and other modifications
apiClient.interceptors.request.use(
  config => {
    try {
      // Block write requests for non-internal plans (frontend safety net while app is read-only)
      const method = (config.method || '').toLowerCase();
      const isWrite = method === 'post' || method === 'put' || method === 'patch' || method === 'delete';
      if (isWrite) {
        const { tier } = getSubscriptionInfo();
        const url = (config.url || '');
        const isAuthOrBilling = url.includes('/auth/') || url.includes('/billing/');
        const isSettings = url.startsWith('/settings');
        if (tier !== 'internal' && !isAuthOrBilling && !isSettings) {
          const err = new Error('Write operations are restricted to internal plan during beta');
          // Attach a recognizable flag
          err.code = 'WRITE_BLOCKED_BY_TIER';
          throw err;
        }
      }

      // Add authentication header if token exists
      // Check for both client token (auth_token) and admin token (admin_token)
      const token = localStorage.getItem('auth_token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('admin_token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      // Skip other processing for requests we should exclude
      if (shouldSkipProcessing(config)) {
        return config;
      }

      // AUTOMATION-SPECIFIC MODIFICATIONS
      if (config.url?.includes('/automations') && (config.method === 'post' || config.method === 'patch') && config.data) {
        // Safely get the data as an object
        let data;
        if (typeof config.data === 'string') {
          try {
            data = JSON.parse(config.data);
          } catch (err) {
            console.error('Error parsing automation data as JSON:', err);
            return config; // Skip further processing if JSON parsing fails
          }
        } else {
          data = { ...config.data }; // Make a shallow copy
        }

        // 1. CRITICAL FIX: Add status field if missing
        if (config.method === 'post' && !data.status) {
          if (isDevelopment) {
            console.log('⚠️ Adding missing status="DRAFT" field to automation request payload');
          }
          data.status = 'DRAFT';
        }

        // 2. Ensure schedule_day is always a string
        if (data.schedule_day !== undefined) {
          const originalValue = data.schedule_day;
          data.schedule_day = String(data.schedule_day);
          if (isDevelopment) {
            console.log(`🔒 Ensuring schedule_day is string: ${originalValue} (${typeof originalValue}) → ${data.schedule_day} (${typeof data.schedule_day})`);
          }
        }

        // 3. Handle schedule_time conversion - REMOVED to prevent double conversion
        // The useAutomations hook now handles TimeValue objects correctly
        if (data.schedule_time !== undefined && isDevelopment) {
          console.log(`⏰ API Client: Passing through schedule_time without conversion: ${data.schedule_time}`);
        }

        // Update the request config with the modified data
        config.data = typeof config.data === 'string' ? JSON.stringify(data) : data;

        if (isDevelopment) {
          console.log('Final automation payload:', JSON.stringify(config.data, null, 2));
        }
      }
    } catch (err) {
      // If any error occurs, log it but allow the request to continue
      console.error('Error in request interceptor:', err);
    }

    return config;
  },
  error => Promise.reject(error)
);

// Add detailed logging for responses and handle subscription expiry
apiClient.interceptors.response.use(
  response => {
    console.group('✅ API Response');
    console.log('URL:', response.config.url);
    console.log('Status:', response.status);
    console.log('Response Data:', response.data);
    console.groupEnd();
    return response;
  },
  error => {
    console.group('❌ API Error');
    console.log('URL:', error.config?.url);
    console.log('Status:', error.response?.status);
    console.log('Error Message:', error.message);
    if (error.response) {
      console.log('Response Data:', error.response.data);
      if (error.response.data?.detail && Array.isArray(error.response.data.detail)) {
        console.log('Validation Errors:');
        error.response.data.detail.forEach((err, index) => {
          console.log(`  ${index + 1}:`, err);
        });
      }
      console.log('Response Headers:', error.response.headers);
    }
    console.groupEnd();

    // Check for JWT token invalidation due to subscription changes
    const isTokenInvalidated =
      error.response?.status === 401 && (
        error.response?.data?.detail?.includes('token') ||
        error.response?.data?.detail?.includes('blacklisted') ||
        error.response?.data?.detail?.includes('invalidated') ||
        error.response?.data?.detail?.includes('subscription')
      );

    // Trigger subscription error notification for token invalidation
    if (isTokenInvalidated) {
      console.warn('🔄 JWT token invalidated due to subscription changes - triggering notification');

      // Dispatch custom event for subscription notifications
      window.dispatchEvent(new CustomEvent('subscriptionError', {
        detail: { error }
      }));
    }

    // Handle subscription expiry - 402 Payment Required or 403 Forbidden for expired subscriptions
    if (error.response?.status === 402 ||
      (error.response?.status === 403 &&
        error.response?.data?.detail?.includes('clinic account is inactive'))) {

      const statusText = error.response?.status === 402 ? '402 Payment Required' : '403 Forbidden (Inactive Account)';
      console.warn(`🚫 Subscription expired (${statusText}) - redirecting to expired subscription page`);

      // Avoid redirecting if we're already on the expired subscription page or login page
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/expired-subscription') &&
        !currentPath.includes('/login') &&
        !currentPath.includes('/pricing')) {

        // Store the attempted URL to potentially redirect back after resubscription
        const attemptedUrl = window.location.href;
        localStorage.setItem('pre_expiry_url', attemptedUrl);

        // Store clinic ID for expired subscription page before clearing tokens
        try {
          const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken');
          if (token) {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const decoded = JSON.parse(jsonPayload);
            if (decoded.clinic_id) {
              localStorage.setItem('expired_clinic_id', decoded.clinic_id);
              console.log('💾 Stored clinic ID for expired subscription page:', decoded.clinic_id);
            }
          }
        } catch (e) {
          console.warn('Could not extract clinic ID from token before expiry redirect:', e);
        }

        // Clear the auth token since it's no longer valid
        localStorage.removeItem('auth_token');
        localStorage.removeItem('authToken');

        // Redirect to expired subscription page
        window.location.href = '/expired-subscription';
        return Promise.reject(error); // Still reject to prevent further processing
      }
    }

    // TEMPORARY DEBUG: Force logout for this specific clinic ID during testing
    // Check if JWT token contains the test clinic ID that we know is expired
    if (error.config?.headers?.Authorization) {
      try {
        const token = error.config.headers.Authorization.replace('Bearer ', '');
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);

        // REMOVED: Manual test logic that was forcing logout for Pabau Demo clinic
        // This was causing issues where valid active subscriptions were being logged out
        // on any API error (including non-auth errors like 422 validation errors)
      } catch (e) {
        // Ignore JWT parsing errors
      }
    }

    // Handle 401 errors for admin endpoints - automatic logout on token expiration
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAdminEndpoint = url.includes('/admin/');

      if (isAdminEndpoint) {
        console.warn('🔒 Admin authentication failed (401) - clearing session and redirecting to login');

        // Clear admin tokens
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        localStorage.removeItem('admin_token_expires');

        // Avoid infinite redirect loops
        const currentPath = window.location.pathname;
        if (!currentPath.includes('/admin/login')) {
          // Delay slightly to ensure console logs are visible
          setTimeout(() => {
            window.location.href = '/admin/login?reason=token_expired';
          }, 100);
        }
      } else {
        // Handle 401 for regular user endpoints
        const detail = error.response?.data?.detail || '';
        const isUserTokenInvalid =
          detail.includes('token') ||
          detail.includes('expired') ||
          detail.includes('invalid');
        const isDeactivated = detail === 'Your account has been deactivated';

        if ((isUserTokenInvalid || isDeactivated) && !window.location.pathname.includes('/login')) {
          const reason = isDeactivated ? 'account_deactivated' : 'token_expired';
          console.warn(`🔒 User authentication failed (401) - ${reason} - redirecting to login`);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('authToken');
          setTimeout(() => {
            window.location.href = `/login?reason=${reason}`;
          }, 100);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Make API client globally available for debugging in development
if (isDevelopment) {
  window.apiClient = apiClient;
}

export default apiClient;